import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap'
import FeatherIcon from 'feather-icons-react'
import Select from 'react-select'
import Swal from 'sweetalert2'
import { printDesktopTestPage } from 'utility/desktopPrintTestPage.util'
import { getDesktopDevice } from 'utility/desktopPrint.util'
import { ensureDesktopService } from 'utility/desktopService.util'
import {
  desktopIdentity,
  deleteReportPrintConfig,
  printConfiguredReport,
  resolveReportPrintConfig,
  sameDesktop,
  saveReportPrintConfig,
} from 'utility/reportPrintConfig.util'

const SETUP_TITLE = 'ตั้งค่าการพิมพ์สำหรับรายงานนี้'

const ReportPrintSettings = forwardRef(
  ({ reportCode, device, printConfig, getPdfBlob, paperSize, disabled = false }, ref) => {
    const [show, setShow] = useState(false)
    const [busy, setBusy] = useState(false)
    const [testing, setTesting] = useState(false)
    const [confirmingClear, setConfirmingClear] = useState(false)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const [currentDevice, setCurrentDevice] = useState(device)
    const [config, setConfig] = useState(printConfig)
    const [selected, setSelected] = useState(null)
    const [copies, setCopies] = useState(1)
    const [autoPrint, setAutoPrint] = useState(false)
    const working = useRef(false)
    const mounted = useRef(true)
    useEffect(() => {
      mounted.current = true
      return () => {
        mounted.current = false
      }
    }, [])
    useEffect(() => {
      setCurrentDevice(device)
      setConfig(printConfig)
    }, [device, printConfig, reportCode])

    const print = async (force = false, preference = null) => {
      if (working.current || disabled) return true
      if (
        force && (!selected || !Number.isInteger(Number(copies)) ||
          Number(copies) < 1 || Number(copies) > 100)
      ) return false
      // No registered preference means preview until the operator sets it up.
      const printDevice = preference ? preference.device : currentDevice
      const printSettings = preference ? preference.printConfig : config
      if (!printDevice || (!force && !printSettings?.autoPrint)) return false
      working.current = true
      setBusy(true)
      setError('')
      setMessage('')
      try {
        const handled = await printConfiguredReport({
          device: printDevice,
          reportCode,
          getPdfBlob,
          ...(paperSize ? { paperSize } : {}),
          force,
          ...(force ? {
            settings: { printerRefId: selected.value, copies: Number(copies) },
          } : {}),
        })
        if (handled && mounted.current) setMessage('ส่งงานพิมพ์เรียบร้อยแล้ว')
        return handled
      } catch (error) {
        if (mounted.current) setError(error.message || 'พิมพ์รายงานไม่สำเร็จ')
        // A failed Desktop job must not also go to browser printing.
        return true
      } finally {
        working.current = false
        if (mounted.current) setBusy(false)
      }
    }
    useImperativeHandle(ref, () => ({
      printConfigured: (preference) => print(false, preference),
    }))

    const setup = async () => {
      if (working.current) return
      working.current = true
      setShow(true)
      setBusy(true)
      setError('')
      setMessage('')
      // A failed setup must not leave a previously saved printer selectable.
      setSelected(null)
      try {
        const status = await ensureDesktopService()
        if (!status?.ok)
          throw new Error('ไม่พบ CK Doctor Desktop กรุณาเปิดโปรแกรมแล้วลองใหม่')
        const loaded = await getDesktopDevice()
        if (!desktopIdentity(loaded).machineName)
          throw new Error('Desktop ไม่ได้ส่งชื่อเครื่องมา')
        const saved = await resolveReportPrintConfig(loaded, reportCode)
        if (!mounted.current) return
        setCurrentDevice(loaded)
        setConfig(saved)
        const printer =
          loaded.printers.find(
            (item) => (item.refId || item.name) === saved?.printerRefId,
          ) ||
          (!saved &&
            (loaded.printers.find((item) => item.isDefault) ||
              loaded.printers[0]))
        setSelected(
          printer
            ? {
                value: printer.refId || printer.name,
                label: printer.displayName || printer.name,
                printer,
              }
            : null,
        )
        setCopies(saved?.copies || 1)
        setAutoPrint(saved?.autoPrint === true)
      } catch (error) {
        if (mounted.current)
          setError(error.message || 'โหลดการตั้งค่าไม่สำเร็จ')
      } finally {
        working.current = false
        if (mounted.current) setBusy(false)
      }
    }

    const testPage = async () => {
      if (working.current || !selected) return
      working.current = true
      setBusy(true)
      setTesting(true)
      setError('')
      setMessage('')
      try {
        const result = await printDesktopTestPage({
          device: currentDevice,
          printerRefId: selected.value,
        })
        if (mounted.current)
          setMessage(
            `ส่งหน้าทดสอบไปที่ ${result?.printerName || selected.label} แล้ว`,
          )
      } catch (error) {
        if (mounted.current)
          setError(error.message || 'พิมพ์หน้าทดสอบไม่สำเร็จ')
      } finally {
        working.current = false
        if (mounted.current) {
          setBusy(false)
          setTesting(false)
        }
      }
    }

    const clearSettings = async () => {
      if (working.current || !config) return
      working.current = true
      setBusy(true)
      setError('')
      setMessage('')
      try {
        setConfirmingClear(true)
        const result = await Swal.fire({
          icon: 'warning',
          title: 'ล้างการตั้งค่าการพิมพ์?',
          text: `ล้างการตั้งค่าพิมพ์ของรายงาน ${reportCode} เฉพาะเครื่อง ${currentDevice?.machineName || '-'} และปิดการพิมพ์อัตโนมัติ`,
          showCancelButton: true,
          confirmButtonText: 'ล้างการตั้งค่า',
          cancelButtonText: 'ยกเลิก',
        })
        if (!mounted.current) return
        setConfirmingClear(false)
        if (!result.isConfirmed) return
        const current = await getDesktopDevice()
        if (!sameDesktop(currentDevice, current))
          throw new Error('เครื่อง Desktop เปลี่ยน กรุณาโหลดการตั้งค่าใหม่')
        await deleteReportPrintConfig(current, reportCode)
        if (!mounted.current) return
        setConfig(null)
        setCurrentDevice(current)
        setSelected(null)
        setCopies(1)
        setAutoPrint(false)
        setMessage('ล้างการตั้งค่ารายงานของเครื่องนี้แล้ว')
      } catch (error) {
        if (mounted.current)
          setError(error?.response?.data?.message || error.message || 'ล้างการตั้งค่าไม่สำเร็จ')
      } finally {
        working.current = false
        if (mounted.current) {
          setBusy(false)
          setConfirmingClear(false)
        }
      }
    }

    const save = async () => {
      if (working.current || !selected) return
      working.current = true
      setBusy(true)
      setError('')
      try {
        const current = await getDesktopDevice()
        if (!sameDesktop(currentDevice, current))
          throw new Error('เครื่อง Desktop เปลี่ยน กรุณาโหลดการตั้งค่าใหม่')
        const printer = current.printers.find(
          (item) => (item.refId || item.name) === selected.value,
        )
        if (!printer)
          throw new Error('ไม่พบเครื่องพิมพ์ที่เลือก กรุณาโหลดการตั้งค่าใหม่')
        const saved = await saveReportPrintConfig(current, reportCode, {
          printerRefId: printer.refId || printer.name,
          printerName: printer.name,
          copies: Number(copies),
          autoPrint,
        })
        if (!mounted.current) return
        setConfig(saved)
        setCurrentDevice(current)
        setShow(false)
        setMessage('บันทึกการตั้งค่าการพิมพ์แล้ว')
      } catch (error) {
        if (mounted.current)
          setError(
            error?.response?.data?.message ||
              error.message ||
              'บันทึกไม่สำเร็จ',
          )
      } finally {
        working.current = false
        if (mounted.current) setBusy(false)
      }
    }

    return (
      <>
        {!show && error && (
          <span role="alert" className="text-danger small mr-2">
            {error}
          </span>
        )}
        {!show && message && (
          <span role="status" className="text-success small mr-2">
            {message}
          </span>
        )}
        <Button
          variant="link"
          size="sm"
          className="text-muted p-1 ml-2"
          title={SETUP_TITLE}
          aria-label={SETUP_TITLE}
          disabled={disabled || busy || !reportCode}
          onClick={setup}
        >
          <FeatherIcon icon="settings" size={16} />
        </Button>
        <Modal
          show={show}
          onHide={() => !busy && setShow(false)}
          backdrop={busy ? 'static' : true}
          keyboard={!busy}
          enforceFocus={!confirmingClear}
        >
          <Modal.Header closeButton={!busy}>
            <Modal.Title>{SETUP_TITLE}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              รายงาน: <strong>{reportCode}</strong>
            </p>
            <p>
              เครื่อง: <strong>{currentDevice?.machineName || '-'}</strong>
            </p>
            {error && <Alert variant="danger">{String(error)}</Alert>}
            {message && <Alert variant="success">{message}</Alert>}
            {busy && <Spinner animation="border" size="sm" className="mb-2" />}
            <Form.Group controlId="report-config-printer">
              <Form.Label>เครื่องพิมพ์</Form.Label>
              <Select
                inputId="report-config-printer"
                value={selected}
                isDisabled={busy}
                isClearable={false}
                placeholder="เลือกเครื่องพิมพ์"
                noOptionsMessage={() => 'ไม่พบเครื่องพิมพ์'}
                options={(currentDevice?.printers || []).map((printer) => ({
                  value: printer.refId || printer.name,
                  label: `${printer.displayName || printer.name}${
                    printer.status ? ' (' + printer.status + ')' : ''
                  }`,
                  printer,
                }))}
                onChange={setSelected}
              />
            </Form.Group>
            <Form.Group controlId="report-config-copies">
              <Form.Label>จำนวนชุด</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={100}
                step={1}
                value={copies}
                disabled={busy}
                onChange={(event) => setCopies(event.target.value)}
              />
            </Form.Group>
            <Form.Check
              id="report-config-auto-print"
              label="พิมพ์ผ่าน Desktop อัตโนมัติเมื่อเปิดรายงาน"
              checked={autoPrint}
              disabled={busy}
              onChange={(event) => setAutoPrint(event.target.checked)}
            />
            <small className="text-muted">
              เมื่อเปิดรายงานและข้อมูลครบแล้ว
              จะส่งพิมพ์ตามเครื่องพิมพ์และจำนวนชุดที่ตั้งไว้
            </small>
            <div className="d-flex flex-wrap align-items-center mt-3">
              <Button
                variant="outline-primary"
                className="mr-2 mb-2"
                title="พิมพ์รายงานผ่าน Desktop ด้วยเครื่องพิมพ์และจำนวนชุดที่เลือก โดยไม่บันทึกการตั้งค่า"
                aria-label="Print Desktop"
                disabled={
                  disabled || busy || !selected ||
                  !Number.isInteger(Number(copies)) ||
                  Number(copies) < 1 || Number(copies) > 100
                }
                onClick={() => print(true)}
              >
                {busy ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FeatherIcon icon="printer" size={16} />
                )}
                <span className="ml-1">Print Desktop</span>
              </Button>
              <Button
                variant="outline-primary"
                className="mb-2"
                title="พิมพ์หน้าทดสอบ 1 ชุด พร้อมข้อความ Hello CK-Doctor Print Test Page และ JSON จาก Desktop"
                disabled={busy || !selected}
                onClick={testPage}
              >
                {testing ? (
                  <Spinner animation="border" size="sm" className="mr-1" />
                ) : (
                  <FeatherIcon icon="printer" size={16} className="mr-1" />
                )}
                Print Test Page
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-danger"
              className="mr-auto"
              title={config ? 'ล้างค่าที่บันทึกไว้สำหรับเครื่องนี้และรายงานนี้' : 'ยังไม่มีการตั้งค่าที่บันทึกไว้'}
              disabled={busy || !config}
              onClick={clearSettings}
            >
              <FeatherIcon icon="trash-2" size={16} className="mr-1" />
              ล้างการตั้งค่า
            </Button>
            <Button
              variant="outline-secondary"
              title="โหลดข้อมูลเครื่องและเครื่องพิมพ์ใหม่"
              disabled={busy}
              onClick={setup}
            >
              โหลดใหม่
            </Button>
            <Button
              variant="secondary"
              title="ปิดการตั้งค่า"
              disabled={busy}
              onClick={() => setShow(false)}
            >
              ยกเลิก
            </Button>
            <Button
              variant="primary"
              title="บันทึกการตั้งค่ารายงานสำหรับเครื่องนี้"
              onClick={save}
              disabled={
                busy ||
                !selected ||
                !Number.isInteger(Number(copies)) ||
                Number(copies) < 1 ||
                Number(copies) > 100
              }
            >
              บันทึก
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    )
  },
)

export default ReportPrintSettings
